import * as ChannelService from "@channel.io/channel-web-sdk-loader";
import useScrollLock from "@repo/hooks/utils/useScrollLock";
import { transformWon } from "@repo/utils/src/number";
import React, { type RefObject, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import Button from "@components/atoms/Button";
import Dim from "@components/atoms/Dim";
import Icon from "@components/atoms/Icon";
import Logo from "@components/atoms/Logo";

import CartLineItem from "@organisms/CartLineItem";

import useCart from "@hooks/reducerHooks/useCart";
import useLayout from "@hooks/reducerHooks/useLayout";
import useUser from "@hooks/reducerHooks/useUser";

import { IconName } from "@const/enum";
import { URLS } from "@const/urls";

function Cart() {
  const navigate = useNavigate();
  const { cart } = useCart();
  const { customer, accessToken, fetchUser } = useUser();
  const { openLoginModal, isCartOpen, closeCart } = useLayout();
  const { t } = useTranslation();

  useEffect(() => {
    ChannelService.updateUser({
      profile: { cartCount: cart.totalQuantity, cartPrice: cart.cost.totalAmount.amount.replace(".0", "") },
    });
  }, [cart.totalQuantity, cart.cost.totalAmount.amount]);

  const cartTotal = useMemo(
    () =>
      cart.lines.edges.reduce((accumulator, record) => accumulator + parseInt(record.node.cost.totalAmount.amount), 0),
    [cart.lines],
  );

  return (
    <>
      <div className="z-aboveChannelTalk pc:w-cart pc:min-w-97.5 pc:px-5 fixed top-0 right-0 h-screen w-full bg-white px-4 pt-2.5 pb-5 text-black">
        <div className="pc:flex pc:items-center pc:justify-between mb-5 grid h-7.5 grid-cols-3">
          <span className="flex items-center gap-1">
            <Icon alt="shoppingBag_700" className="pc:size-5 size-6" name={IconName.ShoppingBag_700} />
            <div className="pc:h-4.5 h-4">{cart.totalQuantity} ITEMS</div>
          </span>
          <Logo className="pc:hidden" />
          <div className="flex items-center">
            <Button className="ml-auto" onClick={closeCart} variant="icon">
              <Icon alt="close_700" className="pc:size-5 size-6" name={IconName.Close_700} />
            </Button>
          </div>
        </div>
        <div className="relative h-[calc(var(--vh,_1vh)_*_100-_60px)] w-full">
          <div className="scrollbar-hide pc:pb-10 absolute size-full overflow-y-scroll pb-5">
            <ul className="mb-10 flex flex-col gap-5">
              {cart.lines.edges.length ? (
                cart.lines.edges.map(({ node }) => (
                  <li key={node.id}>
                    <CartLineItem lineNode={node} />
                  </li>
                ))
              ) : (
                <div className="center absolute flex flex-col items-center gap-2.5 pb-20">
                  Your cart is empty.
                  <Button className="w-max" onClick={closeCart}>
                    Continue Shopping
                  </Button>
                </div>
              )}
            </ul>
            {cart.lines.edges.length ? (
              <div>
                <Button
                  className="flex w-full justify-between"
                  onClick={() => {
                    // 로그인 되어 있지 않을 때
                    // 로그인한 사용자가 본인인증을 하지 않았을 때
                    if (!accessToken || !customer?.phone) {
                      openLoginModal({ redirectUrl: URLS.CHECKOUT });
                    } else {
                      navigate(URLS.CHECKOUT);
                    }
                    closeCart();
                  }}
                  styleType="primary"
                >
                  <span>Checkout Now</span>
                  <span>
                    {transformWon(cartTotal)}
                    {t("cart.currency")}
                  </span>
                </Button>
              </div>
            ) : null}
          </div>
        </div>
      </div>
      <Dim
        onClick={() => {
          isCartOpen && closeCart();
        }}
      />
    </>
  );
}

export default Cart;
